import { useEffect } from 'react';
import { AlertTriangle, ArrowDown, ArrowUp, ChevronDown, ChevronRight, History, Pencil } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Badge } from '@/components/Shared/Badge';
import { Button } from '@/components/Shared/Button';
import { Loading } from '@/components/Shared/Loading';
import { EmptyState } from '@/components/Shared/EmptyState';
import { Pagination } from '@/components/Shared/Pagination';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '@/components/Shared/Table';
import {
  FacilityFlowCoreModulesFleetDtosVehicleLogbookSort as SortColumn,
  FacilityFlowCoreModulesFleetEntitiesVehicleLogbookEntryKind as EntryKind,
} from '@/api/generated/models';
import {
  DEFAULT_LOGBOOK_ORDER,
  LOGBOOK_PAGE_SIZE,
  useVehicleLogbookStore,
  type VehicleLogbookEntryDto,
  type VehicleLogbookFilter,
  type VehicleLogbookSort,
  type VehicleLogbookVehicleDto,
} from '@/store/useVehicleLogbookStore';
import { attentionTexts, describeRevision, driversOf, issueLevel } from '@/pages/VehicleLogbook/logbookText';
import { LogbookIssueLines, LogbookKmSummary, LogbookRouteCell } from '@/pages/VehicleLogbook/LogbookCells';
import { formatDate, formatDateTime, formatUtcToLocalTime, parseUtcTimestamp } from '@/utils/dateUtils';
import { formatKm, formatKmNumber } from '@/utils/distance';

// The table is `table-fixed`: every column but "Fahrer" has a fixed width and the driver gets
// what is left. Its minimum width is the fixed columns visible at each breakpoint plus 176px for
// the driver — narrower containers scroll sideways instead of squeezing the driver to nothing.
// Fixed widths: 40 issue + 104 date + 64 departure + 64 arrival + 64 km + 80 actions = 416,
// md: + 96 + 96 km start/end, lg: + 176 destination; passengers add 128.
const TABLE_MIN_WIDTH = 'min-w-[592px] md:min-w-[784px] lg:min-w-[960px]';
const TABLE_MIN_WIDTH_WITH_PASSENGERS = 'min-w-[720px] md:min-w-[912px] lg:min-w-[1088px]';

interface VehicleLogbookSectionProps {
  section: VehicleLogbookVehicleDto;
  filter: VehicleLogbookFilter;
  open: boolean;
  /** Without it the section has no collapse control (single vehicle). */
  onToggle?: () => void;
  showPassengers: boolean;
  onEdit: (entry: VehicleLogbookEntryDto) => void;
}

/**
 * One vehicle of the logbook (FAFD-26): header with totals and issue counts; while open, its
 * entries page by page from the server.
 */
export default function VehicleLogbookSection({
  section,
  filter,
  open,
  onToggle,
  showPassengers,
  onEdit,
}: VehicleLogbookSectionProps) {
  const { t } = useTranslation(['vehicles', 'common']);
  const vehicleId = section.vehicle?.id ?? '';
  const page = useVehicleLogbookStore((s) => s.pages[vehicleId]);
  const fetchEntries = useVehicleLogbookStore((s) => s.fetchEntries);

  // The store drops all pages when the overview reloads; an open section then loads page 1 again.
  useEffect(() => {
    if (open && vehicleId && !page) fetchEntries(vehicleId, filter, 1);
  }, [open, vehicleId, page, filter, fetchEntries]);

  const issueCount = section.issueCount ?? 0;
  const editedCount = section.editedCount ?? 0;

  const header = (
    <>
      <div className="min-w-0 flex items-center gap-2">
        {onToggle &&
          (open ? (
            <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" strokeWidth={2} aria-hidden />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" strokeWidth={2} aria-hidden />
          ))}
        <span className="font-mono text-[14px] font-semibold text-slate-900">{section.vehicle?.licensePlate}</span>
        {section.vehicle?.description && (
          <span className="hidden sm:inline text-[13px] text-slate-500 truncate">{section.vehicle.description}</span>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-[12px] tabular-nums">
        {issueCount > 0 && (
          <span className="inline-flex items-center gap-1 text-red-600 font-medium">
            <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
            {t('vehicles:logbook.issueCount', { count: issueCount })}
          </span>
        )}
        {editedCount > 0 && (
          <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
            <History className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
            {t('vehicles:logbook.editedCount', { count: editedCount })}
          </span>
        )}
        <span className="text-slate-500">{t('vehicles:logbook.tripCount', { count: section.tripCount ?? 0 })}</span>
        <span className="text-[13px] text-slate-700"><LogbookKmSummary totalKey="logbook.vehicleTotal" total={section.totalKm} business={section.businessKm} private={section.privateKm} /></span>
      </div>
    </>
  );

  const renderEntry = (entry: VehicleLogbookEntryDto) => {
    const level = issueLevel(entry);
    const passengers = entry.passengers ?? [];
    const problems = attentionTexts(entry, t);
    const notes = [
      ...(entry.revisions ?? []).map(
        (r) =>
          `${describeRevision(r, t)} · ${t('vehicles:logbook.edit.changedBy', {
            date: formatDateTime(parseUtcTimestamp(r.changedAt)),
            name: r.changedByName,
          })}`,
      ),
    ];
    const isReading = entry.kind === EntryKind.NUMBER_2;
    // A trip that lost all its stamps in a time clock correction stays visible for the record only.
    const isCancelled = entry.cancelledAt != null;

    return (
      <TableRow key={entry.id} className={isCancelled ? 'text-slate-400 [&_*]:!text-slate-400' : ''}>
        {/* Own fixed-width column, so the table does not shift when a page has no issues. */}
        <TableCell className="!px-2 align-top">
          {level === 'attention' && (
            <AlertTriangle
              className="w-4 h-4 text-red-600"
              strokeWidth={2}
              aria-label={problems.join(', ')}
            />
          )}
          {level === 'edited' && (
            <History className="w-4 h-4 text-amber-500" strokeWidth={2} aria-label={t('vehicles:logbook.noted')} />
          )}
        </TableCell>
        <TableCell className="whitespace-nowrap align-top">
          {formatDate(parseUtcTimestamp(entry.occurredAt))}
        </TableCell>
        <TableCell className="whitespace-nowrap align-top">
          {entry.startedAt ? formatUtcToLocalTime(entry.startedAt) : '—'}
        </TableCell>
        <TableCell className="whitespace-nowrap align-top">
          {!isReading && entry.endedAt ? formatUtcToLocalTime(entry.endedAt) : '—'}
        </TableCell>
        <TableCell className="align-top break-words">
          {isReading ? (
            <>
              <div className="italic text-slate-600">
                {t('vehicles:logbook.readingSetShort', { km: formatKm(entry.startOdometerKm) })}
              </div>
              {entry.startedByName && (
                <div className="text-[11px] text-slate-500">
                  {t('vehicles:logbook.readingBy', { name: entry.startedByName })}
                </div>
              )}
            </>
          ) : (
            <div>{driversOf(entry)}</div>
          )}
          {isCancelled && (
            <div className="mt-1">
              <Badge variant="default">{t('vehicles:logbook.cancelled')}</Badge>
            </div>
          )}
          <LogbookIssueLines entry={entry} />
          {notes.map((note, i) => (
            <div key={i} className="mt-1 text-[11px] text-slate-500">
              {note}
            </div>
          ))}
        </TableCell>
        {showPassengers && (
          <TableCell className="align-top break-words text-[12px] text-slate-600">
            {passengers.length > 0 ? passengers.map((name) => <div key={name}>{name}</div>) : '—'}
          </TableCell>
        )}
        <TableCell className="hidden md:table-cell text-right font-mono align-top">
          {isReading ? '—' : formatKmNumber(entry.startOdometerKm)}
        </TableCell>
        <TableCell className="hidden md:table-cell text-right font-mono align-top">
          {formatKmNumber(isReading ? entry.startOdometerKm : entry.endOdometerKm)}
        </TableCell>
        <TableCell className="text-right font-mono font-medium text-slate-900 align-top">
          {isReading ? '—' : formatKmNumber(entry.distanceKm)}
        </TableCell>
        <TableCell className="hidden lg:table-cell align-top break-words">
          <LogbookRouteCell entry={entry} />
        </TableCell>
        <TableCell className="text-right align-top">
          {!isCancelled && (
            <Button
              variant="ghost"
              size="sm"
              title={t('vehicles:logbook.edit.title')}
              aria-label={t('vehicles:logbook.edit.title')}
              className="p-1 sm:p-2"
              onClick={() => onEdit(entry)}
            >
              <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2} />
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const order = page?.order ?? DEFAULT_LOGBOOK_ORDER;

  /** First click sorts ascending (A–Z, small to large), the next one descending. */
  const changeOrder = (sort: VehicleLogbookSort) =>
    fetchEntries(vehicleId, filter, 1, {
      sort,
      descending: order.sort === sort ? !order.descending : false,
    });

  const sortableHeader = (sort: VehicleLogbookSort, columnKey: string, className = '') => {
    const active = order.sort === sort;
    return (
      <TableHeader
        className={className}
        sortable
        sortDirection={active ? (order.descending ? 'desc' : 'asc') : null}
        onSort={() => changeOrder(sort)}
      >
        {t(`vehicles:logbook.columns.${columnKey}`)}
      </TableHeader>
    );
  };

  const renderBody = () => {
    if (!page || (page.loading && page.items.length === 0)) {
      return (
        <div className="p-4">
          <Loading />
        </div>
      );
    }
    if (page.error) {
      return (
        <div className="p-4">
          <EmptyState
            variant="error"
            title={t('vehicles:logbook.errors.fetchFailed')}
            onRetry={() => fetchEntries(vehicleId, filter, page.page)}
          />
        </div>
      );
    }
    return (
      <>
        {/* Fixed layout: column widths come from the header, so notes wrap instead of widening columns. */}
        <Table className={`table-fixed w-full ${showPassengers ? TABLE_MIN_WIDTH_WITH_PASSENGERS : TABLE_MIN_WIDTH}`}>
          <TableHead>
            <TableRow>
              <TableHeader className="w-10 !px-2">
                <button
                  type="button"
                  onClick={() => changeOrder(SortColumn.NUMBER_10)}
                  aria-sort={
                    order.sort === SortColumn.NUMBER_10 ? (order.descending ? 'descending' : 'ascending') : 'none'
                  }
                  title={t('vehicles:logbook.sortByIssues')}
                  aria-label={t('vehicles:logbook.sortByIssues')}
                  className={`inline-flex items-center gap-0.5 hover:text-slate-900 ${
                    order.sort === SortColumn.NUMBER_10 ? 'text-slate-900' : ''
                  }`}
                >
                  <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
                  {order.sort === SortColumn.NUMBER_10 &&
                    (order.descending ? (
                      <ArrowDown className="w-3 h-3" strokeWidth={2} aria-hidden />
                    ) : (
                      <ArrowUp className="w-3 h-3" strokeWidth={2} aria-hidden />
                    ))}
                </button>
              </TableHeader>
              {sortableHeader(SortColumn.NUMBER_1, 'date', 'w-[104px]')}
              {sortableHeader(SortColumn.NUMBER_2, 'departure', 'w-16')}
              {sortableHeader(SortColumn.NUMBER_3, 'arrival', 'w-16')}
              {sortableHeader(SortColumn.NUMBER_4, 'driver')}
              {showPassengers && sortableHeader(SortColumn.NUMBER_5, 'passengers', 'w-32')}
              {sortableHeader(SortColumn.NUMBER_6, 'startKm', 'hidden md:table-cell w-24 text-right')}
              {sortableHeader(SortColumn.NUMBER_7, 'endKm', 'hidden md:table-cell w-24 text-right')}
              {sortableHeader(SortColumn.NUMBER_8, 'distance', 'w-16 text-right')}
              {sortableHeader(SortColumn.NUMBER_9, 'destinationPurpose', 'hidden lg:table-cell w-44')}
              <TableHeader className="w-20 text-right">{t('common:actions.title')}</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>{page.items.map(renderEntry)}</TableBody>
        </Table>
        <div className="px-3 pb-3">
          <Pagination
            page={page.page}
            pageCount={Math.ceil(page.totalCount / LOGBOOK_PAGE_SIZE)}
            totalCount={page.totalCount}
            onPageChange={(next) => fetchEntries(vehicleId, filter, next)}
            disabled={page.loading}
          />
        </div>
      </>
    );
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      {onToggle ? (
        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className={`w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 text-left hover:bg-slate-50 rounded-xl ${
            open ? 'border-b border-slate-100 rounded-b-none' : ''
          }`}
        >
          {header}
        </button>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-b border-slate-100">
          {header}
        </div>
      )}
      {open && renderBody()}
    </div>
  );
}
